import terms from './terms.json';

export interface GlossaryTerm {
  slug: string;
  name: string;
  /** Other names the term goes by (searchable, shown on the term page). */
  aka?: string[];
  /** A direct 40-60 word definition: the passage answer engines quote. */
  definition: string;
  /** How to read or calculate it, one short paragraph. */
  detail: string;
  /** Plain-text formula where one exists. */
  formula?: string;
  /** Blog post slugs that discuss the term. */
  posts: string[];
  /** Docs paths that cover how Revvly reports it. */
  docs: string[];
  /** Slugs of related glossary terms. */
  related: string[];
}

export const GLOSSARY: GlossaryTerm[] = (terms as GlossaryTerm[])
  .slice()
  .sort((a, b) => a.name.localeCompare(b.name));

export function getTerm(slug: string | undefined): GlossaryTerm | undefined {
  return GLOSSARY.find((t) => t.slug === slug);
}

/** Terms that cite a given blog post: powers the "Key terms" box on each article. */
export function getTermsForPost(postSlug: string): GlossaryTerm[] {
  return GLOSSARY.filter((t) => t.posts.includes(postSlug));
}

const SITE_URL = 'https://www.revvly.ai';
const SET_NAME = 'Revvly SaaS revenue glossary';

const breadcrumb = (items: [string, string][]) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map(([name, item], i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name,
    item,
  })),
});

const termNode = (t: GlossaryTerm, withSet: boolean) => ({
  '@type': 'DefinedTerm',
  '@id': `${SITE_URL}/glossary/${t.slug}`,
  name: t.name,
  ...(t.aka?.length ? { alternateName: t.aka } : {}),
  description: t.definition,
  url: `${SITE_URL}/glossary/${t.slug}`,
  ...(withSet
    ? {
        inDefinedTermSet: {
          '@type': 'DefinedTermSet',
          name: SET_NAME,
          url: `${SITE_URL}/glossary`,
        },
      }
    : {}),
});

export function buildGlossaryIndexJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'DefinedTermSet',
        '@id': `${SITE_URL}/glossary`,
        name: SET_NAME,
        url: `${SITE_URL}/glossary`,
        hasDefinedTerm: GLOSSARY.map((t) => termNode(t, false)),
      },
      breadcrumb([
        ['Home', `${SITE_URL}/`],
        ['Glossary', `${SITE_URL}/glossary`],
      ]),
    ],
  };
}

export function buildTermJsonLd(t: GlossaryTerm) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      termNode(t, true),
      breadcrumb([
        ['Home', `${SITE_URL}/`],
        ['Glossary', `${SITE_URL}/glossary`],
        [t.name, `${SITE_URL}/glossary/${t.slug}`],
      ]),
    ],
  };
}
